import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EmptyState } from '@ets/design-system';
import { useParams } from 'react-router';

import { useDispatch, useEffectOnUpdate, useSelector } from '_common/hooks';
import { PublicLinkService } from '_common/services';

import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { listObjects } from '_common/components/Table/TableSlice';
import { useGetPublicLinkListQuery } from './PublicLinkSettingsApi';

import IntlErrorBoundary from '_common/components/IntlErrorBoundary/IntlErrorBoundary';
import { ThunksTable, Cell, type TableColumn, type TableRow } from '_common/components/Table';

import PublicLinkHeading from './PublicLinkHeading';
import StatusCell from './Cells/StatusCell';
import SettingsCell from './Cells/SettingsCell/SettingsCell';

import styles from './PublicLinkView.module.scss';

const PAGE_IDENTITY = 'publicLink';

const PublicLinkView = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id }: { id: ObjectId } = useParams();
  const { data: dict } = useGetPublicLinkListQuery({
    objectId: id,
    params: {
      offset: 0,
      size: 200,
    },
  });

  const list = useSelector((state) => state.table.identity.publicLink.list);
  const appData = useSelector((state) => state.app.data);

  //#region Table object listing handlers
  const fetchObjects = useCallback(
    async (params: any) => {
      const resp = await new PublicLinkService().listPublicLinks(id, params);
      return {
        ...resp,
        data: { nodes: resp.data },
      };
    },
    [dict, id],
  );

  useEffect(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  useEffectOnUpdate(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'FILTER',
        request: { offset: 0 },
      }),
    );
  }, [fetchObjects]);
  //#endregion

  const handleOnSettingsClick = (object: Objekt) => {
    dispatch(
      openAndUpdateModal({
        modal: 'PublicLinkModal',
        data: {
          linkId: object.id,
          mode: 'edit',
        },
      }),
    );
  };

  const handleOnDeleteClick = (object: Objekt) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          title: 'DELETE_PUBLIC_LINK',
          message: 'DELETE_PUBLIC_LINK_MESSAGE',
          messageValues: { link: object.name },
          actionCode: 'deletePublicLink',
          actionValue: {
            objectId: object.object,
            linkId: object.id,
            linkName: object.name,
          },
          cancelButtonShow: true,
          cancelButtonTextId: 'global.cancel',
          confirmButtonTextId: 'DELETE_PUBLIC_LINK',
          confirmButtonType: 'danger',
          width: 60,
        },
      }),
    );
  };

  const columns = useMemo<TableColumn<Objekt>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        header: intl.formatMessage({ id: 'global.name' }),
        cell: (info) => {
          const object = info.row.original;
          return <Cell testId={`${object.id}-name-column`}>{object.name}</Cell>;
        },
      },
      {
        id: 'dateCreation',
        accessorKey: 'time.creation',
        enableSorting: false,
        size: 200,
        header: intl.formatMessage({ id: 'global.dateCreated' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-date-creation-column`}>
              <Cell.DateTime
                date={{ date: object.time.creation }}
                time={{ time: object.time.creation }}
              />
            </Cell>
          );
        },
      },
      {
        id: 'downloads',
        accessorKey: 'downloads',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'storage.modals.share.downloads' }),
        cell: (info) => {
          const object = info.row.original;
          return <Cell testId={`${object.id}-downloads-column`}>{object.downloads}</Cell>;
        },
      },
      {
        id: 'status',
        enableSorting: false,
        accessorFn: (object) => object.expiration?.active,
        size: 160,
        header: intl.formatMessage({ id: 'storage.browserHeader.status' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-status-column`}>
              <StatusCell object={object} />
            </Cell>
          );
        },
      },
      {
        id: 'copyList',
        enableSorting: false,
        size: 106,
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-clipboard-column`}>
              <SettingsCell object={object} />
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    return list.map<TableRow<Objekt>>((objectId) => {
      const object = appData[objectId];
      return {
        ...object,
        suffix: {
          actions: [
            {
              label: intl.formatMessage({ id: 'global.settings' }),
              onClick: () => {
                handleOnSettingsClick(object);
              },
            },
            {
              label: intl.formatMessage({ id: 'DELETE_PUBLIC_LINK' }),
              onClick: () => {
                handleOnDeleteClick(object);
              },
            },
          ],
        },
      };
    });
  }, [list, appData]);

  const renderEmptyState = () => {
    return (
      <div className={styles.empty}>
        <EmptyState
          size="l"
          icon="folder"
          title="No public links"
          cta={{
            children: intl.formatMessage({ id: 'settings.publicLink.newPublicLink' }),
            onClick: () => {
              dispatch(
                openAndUpdateModal({
                  modal: 'PublicLinkModal',
                  data: {
                    objectId: id,
                    mode: 'create',
                  },
                }),
              );
            },
          }}
          style={{ width: '50rem' }}
          description={<FormattedMessage id="NO_PUBLIC_LINKS_ASSOCIATED_WITH_FILE" />}
          testId="public-link"
        />
      </div>
    );
  };

  return (
    <>
      <div className={styles.publicLinkContainer}>
        <IntlErrorBoundary fallbackType="sectionHeading" mockProps={{ size: 'l' }}>
          <PublicLinkHeading />
        </IntlErrorBoundary>
      </div>

      <ThunksTable
        identity={PAGE_IDENTITY}
        fetchObjects={fetchObjects}
        options={{ columns, data: rowsData }}
        lazyLoading
        renderEmptyState={renderEmptyState}
        testId="publicLinkView"
        selectable={false}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        renderFooter={() => null}
      />
    </>
  );
};

export default PublicLinkView;
